import { ReactNode, useState, useCallback } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { useDebouncedTrackAction } from '@paradigm/features/src/analytics';

type RfqsExpansionState = ReadonlyMap<string, boolean>;

type ToggleRfqExpansion = (rfqId: string) => void;

const INITIAL_STATE = new Map<string, boolean>();

const [useExpansionState, RfqsExpansionStateProvider] =
  createSafeContext<RfqsExpansionState>('RfqsExpansionState');

const [useToggleRfqExpansion, ToggleRfqExpansionProvider] =
  createSafeContext<ToggleRfqExpansion>('ToggleRfqExpansion');

function useRfqExpansionState(rfqId: string): boolean {
  const ctx = useExpansionState();
  return ctx.get(rfqId) ?? false;
}

interface Props {
  readonly children: ReactNode;
}

function RfqExpansionProvider({ children }: Props) {
  const [rfqsExpansionState, setRfqsExpansionState] = useState(INITIAL_STATE);
  const track = useDebouncedTrackAction();

  const toggleRfqExpansion = useCallback(
    (rfqId: string) =>
      setRfqsExpansionState((prevState) => {
        const wasExpanded = prevState.get(rfqId) ?? false;
        const isExpanded = !wasExpanded;
        track(isExpanded ? 'expand market row' : 'collapse market row');
        return new Map(prevState).set(rfqId, !wasExpanded);
      }),
    [track],
  );

  return (
    <RfqsExpansionStateProvider value={rfqsExpansionState}>
      <ToggleRfqExpansionProvider value={toggleRfqExpansion}>
        {children}
      </ToggleRfqExpansionProvider>
    </RfqsExpansionStateProvider>
  );
}

export {
  RfqExpansionProvider,
  useExpansionState,
  useRfqExpansionState,
  useToggleRfqExpansion,
};
